import type { CategoryFolderPhase, CategoryNode } from '@dataroom/shared';
import { isInstitutionLevel, pathIdsTo } from '@dataroom/shared';

/**
 * Where the step-by-step filing is in the hierarchy (owner ask 2026-10-15: "where
 * we are in the hierarchy should be much clearer"): a breadcrumb from the top —
 * All categories › Phase › Category › … — whose crumbs step back up to that
 * level to choose again from there. Stepping back is a VIEW of another level:
 * nothing is saved until an option is chosen, and choosing one files the
 * document along that option's path (replacing the deeper levels it was in).
 * The phase (Financial / Legal / HR / Risk / Technology) is the checklist's
 * grouping of the top-level categories, from GET …/categories `phases` — the
 * same mapping the folder tree uses (lib/folder-phases). Pure helpers.
 */

/** Which level the filing step shows. */
export type LevelView =
  /** Continue from the deepest category the document is filed in (the default). */
  | { kind: 'filed' }
  /** Every top-level category. */
  | { kind: 'top' }
  /** One checklist phase's top-level categories. */
  | { kind: 'phase'; phaseId: string }
  /** The children of one category. */
  | { kind: 'category'; id: string };

export interface TrailCrumb {
  key: string;
  label: string;
  /** The level this crumb steps back to. */
  view: LevelView;
}

/** The checklist phase holding top-level category `topId`, or null. */
export function phaseOfTop(
  topId: string | null | undefined,
  phases: readonly CategoryFolderPhase[] | undefined,
): CategoryFolderPhase | null {
  if (!topId || !phases) return null;
  return phases.find((p) => p.categoryIds.includes(topId)) ?? null;
}

/**
 * The breadcrumb to a level whose options are being chosen: All categories, the
 * phase (when the room's checklist has one for this branch), then every
 * category from the top down to `parentId` (null = the top level).
 */
export function filingTrail(
  view: { parentId: string | null; phaseId?: string | null },
  parentOf: ReadonlyMap<string, string | null>,
  names: ReadonlyMap<string, string>,
  phases: readonly CategoryFolderPhase[] | undefined,
): TrailCrumb[] {
  const crumbs: TrailCrumb[] = [{ key: 'top', label: 'All categories', view: { kind: 'top' } }];
  const ids = view.parentId ? pathIdsTo(view.parentId, parentOf) : [];
  const phase = view.phaseId
    ? (phases?.find((p) => p.id === view.phaseId) ?? null)
    : phaseOfTop(ids[0], phases);
  if (phase) {
    crumbs.push({
      key: `phase:${phase.id}`,
      label: phase.name,
      view: { kind: 'phase', phaseId: phase.id },
    });
  }
  for (const id of ids) {
    crumbs.push({ key: id, label: names.get(id) ?? id, view: { kind: 'category', id } });
  }
  return crumbs;
}

/** A filed category's full path as people read it: "Legal › Contracts › NDAs". */
export function trailLabel(
  id: string,
  parentOf: ReadonlyMap<string, string | null>,
  names: ReadonlyMap<string, string>,
  phases: readonly CategoryFolderPhase[] | undefined,
): string {
  const ids = pathIdsTo(id, parentOf);
  const phase = phaseOfTop(ids[0], phases);
  return [...(phase ? [phase.name] : []), ...ids.map((i) => names.get(i) ?? i)].join(' › ');
}

/** The reviewRound `from` for a view (undefined = the deepest filed category). */
export function roundFrom(
  view: LevelView,
  phases: readonly CategoryFolderPhase[] | undefined,
): { parentId: string | null; onlyIds?: ReadonlySet<string> } | undefined {
  switch (view.kind) {
    case 'filed':
      return undefined;
    case 'top':
      return { parentId: null };
    case 'phase': {
      const phase = phases?.find((p) => p.id === view.phaseId);
      return { parentId: null, onlyIds: new Set(phase?.categoryIds ?? []) };
    }
    case 'category':
      return { parentId: view.id };
  }
}

/** The category tree's levels, counted from the top-level categories (1). */
export function levelNumber(
  parentId: string | null,
  parentOf: ReadonlyMap<string, string | null>,
): number {
  return parentId ? pathIdsTo(parentId, parentOf).length + 1 : 1;
}

/**
 * The document's category set once `optionId` is chosen at the level under
 * `levelParentId`. Under the deepest filed category it is one level deeper, the
 * rest kept (a review round). At a level the person stepped back up to, the
 * path to the option replaces the deepest path (the deeper levels were the
 * wrong way); a second place it is filed in is kept. Only `levelParentId`'s
 * path is looked up, so an option created a moment ago works too.
 */
export function idsWhenChoosing(
  filedIds: readonly string[],
  deepestId: string | null,
  parentOf: ReadonlyMap<string, string | null>,
  levelParentId: string | null,
  optionId: string,
): string[] {
  if (levelParentId === deepestId) return [...new Set([...filedIds, optionId])];
  const onDeepest = new Set(deepestId ? pathIdsTo(deepestId, parentOf) : []);
  const kept = filedIds.filter((id) => !onDeepest.has(id));
  const path = levelParentId ? pathIdsTo(levelParentId, parentOf) : [];
  return [...new Set([...kept, ...path, optionId])];
}

/**
 * Whether "+ New category here" is offered at the level under `parentId`. Not
 * in the ACCOUNT TREE — the category holding "[Institution]" (Bank Docs) and
 * everything below it (banks, statement types, accounts): those categories are
 * made from the room's accounts (ADR-0037), not typed in.
 */
export function offersNewCategoryAt(
  parentId: string | null,
  byId: ReadonlyMap<string, CategoryNode>,
  parentOf: ReadonlyMap<string, string | null>,
): boolean {
  if (parentId === null) return true;
  for (const id of pathIdsTo(parentId, parentOf)) {
    const node = byId.get(id);
    if (!node || isInstitutionLevel(node.name)) return false;
    if (node.children.some((c) => isInstitutionLevel(c.name))) return false;
  }
  return true;
}
